import React, { useState, useEffect } from 'react';
import { Pipeline, pipelineAPI } from '../../services/api';
import { Modal, Button, Card, CardBody } from '../ui';

interface PipelineModalProps {
  pipeline: Pipeline | null;
  onClose: () => void;
}

const PipelineModal: React.FC<PipelineModalProps> = ({ pipeline, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    isDefault: false,
    order: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pipeline) {
      setFormData({
        name: pipeline.name,
        description: pipeline.description || '',
        isActive: pipeline.isActive,
        isDefault: pipeline.isDefault,
        order: pipeline.order,
      });
    }
  }, [pipeline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (pipeline) {
        await pipelineAPI.update(pipeline.id, formData);
      } else {
        await pipelineAPI.create(formData);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save pipeline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={pipeline ? 'Edit Pipeline' : 'Create Pipeline'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Card variant="bordered" className="border-error-200 bg-error-50">
            <CardBody className="p-3">
              <p className="text-sm text-error-600">{error}</p>
            </CardBody>
          </Card>
        )}

        <div>
          <label className="block text-sm font-medium text-neutral-700">
            Pipeline Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="e.g., Sales Pipeline"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            placeholder="Describe the purpose of this pipeline"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">
            Display Order
          </label>
          <input
            type="number"
            min="0"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
          />
        </div>

        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-neutral-900">
              Active
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isDefault"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
            />
            <label htmlFor="isDefault" className="ml-2 block text-sm text-neutral-900">
              Set as Default
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={loading}>
            {pipeline ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PipelineModal;

